import { ColumnTable, DisplayInfo, SearchBox, Table } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Input, Row } from 'reactstrap';

import SectionTitle from '../../../../../../components/text/SectionTitle';
import { moneyFormatter } from '../../../../../../utils/currency';
import { listEligibleDebts } from '../../api';
import { EligibleDebt } from '../../types';

const dictPrefix =
  'financialPortal.currentAccount.tabs.currentAccount.applyRefund.discount';

interface Props {
  studentId: number;
  refundAmount: number;
}

interface SearchForm {
  concept: string;
}

export default function DiscountSubForm({ studentId, refundAmount }: Props) {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();

  const searchFormApi = useForm<SearchForm>({ defaultValues: { concept: '' } });
  const {
    control: searchControl,
    watch: watchSearch,
    setValue: setSearchValue,
  } = searchFormApi;
  const conceptSearch = watchSearch('concept');

  const [debts, setDebts] = useState<EligibleDebt[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const watchedAllocations = watch('allocationsMap');
  const allocations = useMemo<Record<string, string | number>>(
    () => (watchedAllocations ?? {}) as Record<string, string | number>,
    [watchedAllocations],
  );

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    listEligibleDebts(studentId).then(({ data }) => {
      if (!mounted) return;
      setDebts(data ?? []);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [studentId]);

  const filteredDebts = useMemo(() => {
    const q = (conceptSearch ?? '').trim().toLowerCase();
    if (!q) return debts;
    return debts.filter((d) => (d.description ?? '').toLowerCase().includes(q));
  }, [debts, conceptSearch]);

  const totalAllocated = useMemo<number>(() => {
    return Object.values(allocations).reduce<number>((sum, raw) => {
      const value = Number(raw ?? 0);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }, [allocations]);

  useEffect(() => {
    setValue('totalAmount', totalAllocated);
  }, [totalAllocated, setValue]);

  const today = dayjs().startOf('day');

  const columns: ColumnTable<EligibleDebt>[] = [
    {
      headerText: t(`${dictPrefix}.columns.concept`),
      columnName: 'description',
      cellFormat: ({ value }) => value ?? '-',
    },
    {
      headerText: t(`${dictPrefix}.columns.dueDate`),
      columnName: 'endDate',
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ value }) => {
        if (!value) return '-';
        const date = dayjs(value).utc();
        const overdue = date.isBefore(today);
        return (
          <span
            style={overdue ? { color: '#9B1C1C', fontWeight: 600 } : undefined}
          >
            {date.format('DD/MM/YYYY')}
          </span>
        );
      },
    },
    {
      headerText: t(`${dictPrefix}.columns.debt`),
      columnName: 'amount',
      tdClassName: 'text-right',
      thClassName: 'text-right',
      cellFormat: ({ value }) =>
        moneyFormatter().format(Math.ceil(Number(value ?? 0))),
    },
    {
      headerText: t(`${dictPrefix}.columns.allocate`),
      columnName: 'id',
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        const debtAmount = Number(row.amount ?? 0);
        const current = allocations[row.id] ?? '';
        return (
          <div className="d-flex justify-content-center">
            <Input
              type="number"
              min={0}
              max={debtAmount}
              step="0.01"
              value={current as string}
              onChange={(e) => {
                const next = { ...allocations };
                const raw = e.target.value;
                if (raw === '' || Number(raw) <= 0) {
                  delete next[row.id];
                } else {
                  const capped = Math.min(Number(raw), debtAmount);
                  next[row.id] = capped;
                }
                setValue('allocationsMap', next, { shouldDirty: true });
              }}
              style={{ maxWidth: 120 }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Row className="mb-3">
        <Col xs={12}>
          <SectionTitle text={t(`${dictPrefix}.sectionTitle`)} />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={12} md={6}>
          <SearchBox
            name="concept"
            control={searchControl}
            label={t(`${dictPrefix}.search.label`)}
            placeholder={t(`${dictPrefix}.search.placeholder`)}
            clear={() => setSearchValue('concept', '')}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="mb-3">
          <Table
            isLoadingResults={loading}
            data={filteredDebts}
            columns={columns}
            noResultsText={
              <div className="d-flex justify-content-center align-items-center py-4">
                <DisplayInfo
                  iconName="information"
                  textBody={t(`${dictPrefix}.noEligibleDebts`)}
                  maxWidth="500px"
                />
              </div>
            }
          />
        </Col>
        <Col
          xs={12}
          className="d-flex justify-content-end align-items-center py-3 px-3 mb-2"
          style={{ backgroundColor: '#E0EEFD' }}
        >
          <span className="text-primary text-uppercase fw-700 mr-3">
            {t(`${dictPrefix}.totalAllocated`)}
          </span>
          <span className="text-primary fw-700 fs-18">
            {moneyFormatter().format(Math.ceil(totalAllocated))}
          </span>
        </Col>
        <Col xs={12} className="text-right">
          <small className="text-muted">
            {t(`${dictPrefix}.refundAvailable`)}:{' '}
            {moneyFormatter().format(Math.ceil(refundAmount))}
          </small>
        </Col>
      </Row>
    </>
  );
}
